import React from 'react';
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  Heading,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Skeleton,
  Button,
  Tooltip,
  Divider,
} from '@chakra-ui/react';
import {
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { useGetSettlementSummary, useGetCompanyWallet } from '../../../services/payrollService';
import { useGetPlanInfo } from '../../../services/planService';

// Helper function to format currency with currency code
const formatCurrencyWithCode = (amount, currencyCode) => {
  if (amount === null || amount === undefined) return '0';
  
  const currencySymbols = {
    'USD': '$',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'KES': 'KSh',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  const numericAmount = parseFloat(amount) || 0;
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

const SettlementOverview = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");
  
  // Get settlement summary
  const { 
    data: settlementSummaryData, 
    isLoading: settlementSummaryLoading, 
    error: settlementSummaryError,
    refetch: refetchSettlementSummary
  } = useGetSettlementSummary();
  
  // Get wallet balance
  const { 
    data: walletData, 
    isLoading: walletLoading 
  } = useGetCompanyWallet();
  
  const { planInfo } = useGetPlanInfo();
  
  // Extract data
  const settlementSummary = settlementSummaryData?.data || settlementSummaryData;
  const wallet = walletData?.data || walletData;
  
  const walletBalance = wallet?.available_balance ?? wallet?.balance ?? wallet?.current_balance ?? 0;
  const walletCurrency = wallet?.currency || settlementSummary?.wallet_currency || 'NGN';
  
  const summary = settlementSummary?.summary || {};
  const categories = settlementSummary?.categories || [];

  // Calculate statistics
  const totalPending = summary?.total_pending_settlement || 0;
  const totalReadyToPay = summary?.total_ready_to_pay || 0;
  const totalNotConfigured = summary?.total_not_configured || 0;
  const configuredCount = summary?.configured_categories || 0;
  const unconfiguredCount = summary?.unconfigured_categories || 0;
  const totalCategories = summary?.categories_count || 0;

  // Loading state
  if (settlementSummaryLoading || walletLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
              <Skeleton height="20px" mb={2} />
              <Skeleton height="30px" mb={2} />
              <Skeleton height="16px" />
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    );
  }

  // Error state
  if (settlementSummaryError) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <AlertTitle>Failed to load settlement summary</AlertTitle>
        <AlertDescription>
          {settlementSummaryError?.message || "Unable to retrieve settlement information. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Wallet Balance & Summary Stats */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Wallet Balance</StatLabel>
          <StatNumber color="blue.500">
            {formatCurrencyWithCode(walletBalance, walletCurrency)}
          </StatNumber>
          <StatHelpText>
            Available for payments
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Pending Settlement</StatLabel>
          <StatNumber color="orange.500">
            {formatCurrencyWithCode(totalPending, walletCurrency)}
          </StatNumber>
          <StatHelpText>
            From completed batches
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Ready to Pay</StatLabel>
          <StatNumber color="green.500">
            {formatCurrencyWithCode(totalReadyToPay, walletCurrency)}
          </StatNumber>
          <StatHelpText>
            Accounts configured
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Not Configured</StatLabel>
          <StatNumber color="red.500">
            {formatCurrencyWithCode(totalNotConfigured, walletCurrency)}
          </StatNumber>
          <StatHelpText>
            Missing accounts
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Configuration Status */}
      <Box bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <HStack spacing={6}>
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" color="gray.600">Total Categories</Text>
            <Text fontSize="2xl" fontWeight="700" color="gray.800">
              {totalCategories}
            </Text>
          </VStack>
          <Divider orientation="vertical" height="40px" />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" color="gray.600">Configured</Text>
            <HStack>
              <FiCheckCircle color="green" />
              <Text fontSize="2xl" fontWeight="700" color="green.600">
                {configuredCount}
              </Text>
            </HStack>
          </VStack>
          <Divider orientation="vertical" height="40px" />
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" color="gray.600">Not Configured</Text>
            <HStack>
              <FiAlertCircle color="orange" />
              <Text fontSize="2xl" fontWeight="700" color="orange.600">
                {unconfiguredCount}
              </Text>
            </HStack>
          </VStack>
        </HStack>
      </Box>

      {/* Categories Table */}
      {categories.length > 0 ? (
        <Box bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor} overflow="hidden">
          <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
            <Heading size="sm" color="gray.800">
              Settlement Categories
            </Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              View pending amounts by category
            </Text>
          </Box>
          
          <TableContainer>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Category Name</Th>
                  <Th>Category Type</Th>
                  <Th>Pending Amount</Th>
                  <Th>Account Status</Th>
                  <Th>Payment Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {categories.map((category, index) => (
                  <Tr key={index}>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {category.category_name}
                      </Text>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={category.category_type === 'TAX' ? 'blue' : 'purple'}
                        variant="subtle"
                      >
                        {category.category_type}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {formatCurrencyWithCode(category.pending_amount || 0, category.currency || walletCurrency)}
                      </Text>
                    </Td>
                    <Td>
                      {category.account_configured ? (
                        <Badge colorScheme="green" variant="subtle">
                          <HStack spacing={1}>
                            <FiCheckCircle />
                            <Text>Configured</Text>
                          </HStack>
                        </Badge>
                      ) : (
                        <Badge colorScheme="orange" variant="subtle">
                          <HStack spacing={1}>
                            <FiAlertCircle />
                            <Text>Not Configured</Text>
                          </HStack>
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      {category.ready_to_pay ? (
                        <Badge colorScheme="green" variant="subtle">
                          Ready to Pay
                        </Badge>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle">
                          Not Ready
                        </Badge>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>No Settlement Categories</AlertTitle>
            <AlertDescription>
              No pending settlement amounts found. Settlement amounts will appear here after payroll batches are completed.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Action Hint */}
      {totalReadyToPay > 0 && (
        <Alert status="success" borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Ready for Payment</AlertTitle>
            <AlertDescription>
              You have {formatCurrencyWithCode(totalReadyToPay, walletCurrency)} ready to be paid out. 
              Go to the "Settlement Clearing" tab to process payments.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {totalNotConfigured > 0 && (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Account Configuration Required</AlertTitle>
            <AlertDescription>
              {formatCurrencyWithCode(totalNotConfigured, walletCurrency)} cannot be paid because settlement accounts are not configured. 
              Configure accounts in the "Tax Account" tab or update deduction settings.
            </AlertDescription>
          </Box>
        </Alert>
      )}
    </VStack>
  );
};

export default SettlementOverview;

